import React, { useEffect, useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal } from 'react-bootstrap';
import { getSmartEventData } from '../../../../utility/EventEngine';

// ─── AMBIENT DRIFT — tiny dots that rise gently from the bottom corners ───
const drift = keyframes`
  0%   { transform: translateY(0)   translateX(0)   scale(0.6); opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 0.6; }
  100% { transform: translateY(-80px) translateX(var(--dx, 12px)) scale(1.2); opacity: 0; }
`;

// Subtle pulse for the corner badge
const gentlePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--badge-glow); }
  50%       { box-shadow: 0 0 0 8px transparent; }
`;

// ─── LAYER: fixed, pointer-events none, only visible in margins ───
const EffectLayer = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
`;

// Each particle: a tiny glowing dot (no emoji)
const Dot = styled.span`
  position: absolute;
  width: var(--size, 5px);
  height: var(--size, 5px);
  border-radius: 50%;
  background: var(--color, #FFD700);
  bottom: -10px;
  animation: ${drift} var(--dur, 4s) ease-in-out var(--delay, 0s) infinite;
  opacity: 0;
  filter: blur(0.5px);
  /* Keep particles near the side edges so they never cross content */
  left: var(--x, 5%);
`;

// Small corner badge showing the event icon — subtle, no bounce
const CornerBadge = styled.div`
  position: fixed;
  bottom: 64px;
  right: 18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  pointer-events: none;
  z-index: 10001;
  border: 1.5px solid var(--badge-border, rgba(0,0,0,0.06));
  animation: ${gentlePulse} 3s ease-in-out infinite;
  opacity: 0.85;
`;

const SmartHolidaySystem = ({ userJoinedDate, debugOverrideDate, userName }) => {
  const [event, setEvent] = useState(null);
  const [showGreeting, setShowGreeting] = useState(false);

  useEffect(() => {
    const data = getSmartEventData(userJoinedDate, debugOverrideDate, userName);
    if (data) {
      setEvent(data);
      const storageKey = `seen_${data.id}_${new Date().getDate()}`;
      if (!localStorage.getItem(storageKey) && data.founderMsg) {
        setShowGreeting(true);
        localStorage.setItem(storageKey, 'true');
      }
    }
  }, [userJoinedDate, debugOverrideDate, userName]);

  // ── Ambient dots: 10 left-edge + 10 right-edge, never in the centre ──
  const particles = useMemo(() => {
    if (!event) return [];
    const color = event.theme.colors[0] || '#FFD700';
    const dots = [];
    const total = 18; // low count — subtle

    for (let i = 0; i < total; i++) {
      // Alternate left / right edge bands (0–18% and 82–100%)
      const side = i % 2 === 0;
      const x = side
        ? `${Math.random() * 16}%`        // left band
        : `${82 + Math.random() * 16}%`;  // right band

      const size = `${Math.random() * 3 + 3}px`; // 3–6px
      const dur  = `${Math.random() * 5 + 5}s`;  // 5–10s — slow
      const delay = `${Math.random() * 8}s`;
      const dx = `${(Math.random() - 0.5) * 20}px`;
      const opacity = 0.18 + Math.random() * 0.12; // 0.18–0.30 max — barely visible

      dots.push(
        <Dot
          key={i}
          style={{
            '--color': color,
            '--size': size,
            '--dur': dur,
            '--delay': delay,
            '--x': x,
            '--dx': dx,
            opacity,
          }}
        />
      );
    }
    return dots;
  }, [event]);

  if (!event) return null;

  const primaryColor = event.theme.colors[0];
  const secondaryColor = event.theme.colors[1] || primaryColor;

  return (
    <>
      {/* ── Ambient particle layer ── */}
      <EffectLayer>{particles}</EffectLayer>

      {/* ── Tiny corner badge (no bounce, just subtle pulse) ── */}
      <CornerBadge
        style={{
          '--badge-glow': `${primaryColor}30`,
          '--badge-border': `${primaryColor}40`,
        }}
        title={event.title}
      >
        {event.theme.symbols[0]}
      </CornerBadge>

      {/* ── Greeting Modal ── */}
      <Modal
        show={showGreeting}
        onHide={() => setShowGreeting(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 bg-transparent shadow-none"
      >
        <div
          className="position-relative overflow-hidden"
          style={{
            background: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.14)',
            border: `1px solid ${primaryColor}18`,
          }}
        >
          {/* Header strip — slim gradient, no particles inside modal */}
          <div
            style={{
              height: '90px',
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              paddingBottom: '0',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                marginBottom: '-32px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: `2px solid ${primaryColor}20`,
              }}
            >
              {event.theme.symbols[0]}
            </div>
          </div>

          {/* Body */}
          <div className="text-center px-5 pb-5 pt-5">
            <h2
              className="fw-bold mt-3 mb-3"
              style={{ color: primaryColor, fontSize: '1.35rem', letterSpacing: '-0.3px' }}
            >
              {event.title}
            </h2>

            <p
              className="text-muted mb-0"
              style={{
                fontSize: '0.97rem',
                lineHeight: '1.75',
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontStyle: 'italic',
              }}
            >
              {event.founderMsg}
            </p>

            {/* Signature */}
            <div className="mt-4 d-flex flex-column align-items-center">
              <div
                style={{
                  fontFamily: '"Brush Script MT", "Cursive", cursive',
                  fontSize: '1.6rem',
                  color: '#334155',
                  transform: 'rotate(-1.5deg)',
                }}
              >
                Jafran Jemal
              </div>
              <div
                style={{
                  fontSize: '0.62rem',
                  color: '#94a3b8',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginTop: '2px',
                }}
              >
                Founder &amp; Lead Engineer
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => setShowGreeting(false)}
              className="btn mt-4 px-5 py-2 fw-semibold rounded-pill"
              style={{
                background: primaryColor,
                color: '#ffffff',
                border: 'none',
                fontSize: '0.88rem',
                letterSpacing: '0.04em',
                boxShadow: `0 4px 14px ${primaryColor}40`,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={e => (e.target.style.opacity = '0.88')}
              onMouseLeave={e => (e.target.style.opacity = '1')}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SmartHolidaySystem;